import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, AlertTriangle, Phone, Mail, Eye, Flag,
  UserX, Lock, MapPin, CheckCircle
} from "lucide-react";

const EMAIL = "techieroshan@gmail.com";
const COMPANY = "Kosansh Solutions Inc";
const DBA = "ActivitySports.com";

const safetyTips = [
  {
    icon: Eye,
    title: "Verify before you meet",
    body: "Check that your match has a profile photo, completed profile, and — ideally — a verified badge. Look at their completed sessions count and read any reviews.",
  },
  {
    icon: MapPin,
    title: "Meet in a public venue",
    body: "Always meet your new partner at a public, staffed sports facility (club, recreation center, public park court). Never accept invitations to private homes or isolated locations for a first meeting.",
  },
  {
    icon: Phone,
    title: "Tell someone where you're going",
    body: "Share the venue name, time, and your match's profile with a trusted friend or family member before your first session. Consider sharing your live location.",
  },
  {
    icon: Lock,
    title: "Keep personal info private until trust is established",
    body: "Use our in-app messaging for communication. Avoid sharing your home address, personal phone number, or financial details with new matches until you've established trust over multiple public sessions.",
  },
  {
    icon: AlertTriangle,
    title: "Trust your instincts",
    body: "If something feels wrong — cancel. You are never obligated to proceed with a session. There is no penalty for cancelling, and your safety always comes first.",
  },
  {
    icon: Flag,
    title: "Report and block bad actors",
    body: "Use the Report button on any profile or message. Block users who make you uncomfortable. Our Trust & Safety team reviews all reports within 24 hours.",
  },
];

const reportingCategories = [
  { label: "Harassment or threatening behavior", urgency: "high" },
  { label: "Fake profile / impersonation", urgency: "high" },
  { label: "Spam or commercial solicitation", urgency: "medium" },
  { label: "Inappropriate or offensive content", urgency: "medium" },
  { label: "Misleading skill rating", urgency: "low" },
  { label: "No-show / repeated cancellations", urgency: "low" },
  { label: "Suspected minor (under 13)", urgency: "high" },
  { label: "Real-world assault or safety emergency", urgency: "emergency" },
];

const urgencyColors = {
  high: "bg-red-50 border-red-200 text-red-700",
  medium: "bg-yellow-50 border-yellow-200 text-yellow-700",
  low: "bg-gray-50 border-gray-200 text-gray-600",
  emergency: "bg-red-100 border-red-400 text-red-800 font-bold",
};

export default function SafetyCenterPage() {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero */}
      <div className="bg-brand-navy text-white py-14 px-4 dark-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-brand-green text-sm font-bold uppercase tracking-widest mb-3">
            <ShieldCheck size={14} /> Trust & Safety
          </div>
          <h1 className="text-4xl md:text-5xl font-hero font-black mb-3">Safety Center</h1>
          <p className="text-white/70 max-w-2xl">
            {DBA} is built on the principle that sport should be safe, welcoming, and trusted.
            Here's how we protect you and what you can do to stay safe.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-14 space-y-16">

        {/* Emergency Banner */}
        <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-6 flex items-start gap-4">
          <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h2 className="text-lg font-bold text-red-800 mb-1">In an emergency or immediate danger?</h2>
            <p className="text-red-700 text-sm mb-3">
              Call <strong>911</strong> (US) or your local emergency number immediately. Do not wait —
              your safety is the absolute priority. Our platform cannot respond in real time.
            </p>
            <a href={`mailto:${EMAIL}?subject=URGENT Safety Report`} className="inline-flex items-center gap-2 bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-red-700 transition-colors">
              <Mail size={14} /> Urgent Report to Trust &amp; Safety Team
            </a>
          </div>
        </div>

        {/* Our Safety Architecture */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">Our Safety Architecture</h2>
          <p className="text-gray-600 mb-8">
            We take a layered approach to safety. Here's what {DBA} does on your behalf:
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                icon: ShieldCheck,
                title: "Photo Verification",
                desc: "Users can submit a photo for manual review by our Trust & Safety team. Verified badges give you extra confidence. We review all submissions within 48 hours.",
              },
              {
                icon: UserX,
                title: "Report & Block",
                desc: "Every profile and every message thread has a Report button. Reports go directly to our Trust & Safety queue. Blocking prevents all future contact.",
              },
              {
                icon: Lock,
                title: "Private-by-Default Messaging",
                desc: "Your personal contact details are never exposed. All communication happens inside our encrypted in-app messaging system until you choose to share externally.",
              },
              {
                icon: Flag,
                title: "Content Moderation",
                desc: "Profile photos and bio content are subject to automated screening and manual review. Violating content is removed within 24 hours of report.",
              },
              {
                icon: AlertTriangle,
                title: "Proactive Account Monitoring",
                desc: "Our systems flag unusual behavior patterns — such as mass messaging, account age anomalies, or rapid profile changes — for human review.",
              },
              {
                icon: Eye,
                title: "Skill Verification (Coming Soon)",
                desc: "We are developing a skill rating verification system where coaches and club pros can endorse player ratings, reducing misrepresentation.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-2xl p-5 flex gap-4 hover:border-brand-navy/30 transition-colors shadow-sm">
                <div className="w-10 h-10 bg-brand-navy/5 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="text-brand-navy" size={18} />
                </div>
                <div>
                  <h3 className="font-bold text-brand-navy mb-1 text-sm">{title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Safety Tips */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">Personal Safety Tips for Every Session</h2>
          <p className="text-gray-600 mb-8">
            Technology can only do so much. These habits keep you safe in the real world:
          </p>
          <div className="space-y-4">
            {safetyTips.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="bg-white border border-gray-200 rounded-2xl p-5 flex gap-4 shadow-sm">
                <div className="flex-shrink-0 w-8 h-8 bg-brand-green/10 rounded-lg flex items-center justify-center">
                  <Icon className="text-brand-green" size={16} />
                </div>
                <div>
                  <h3 className="font-bold text-brand-navy mb-1 text-sm">{i + 1}. {title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reporting Categories */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">How to Report a Problem</h2>
          <p className="text-gray-600 mb-6">
            You can report from inside the app (every profile has a Report button) or directly by email.
            Here's what we can help with and our target response times:
          </p>
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-3 bg-brand-navy/5 px-5 py-3 text-xs font-bold uppercase tracking-widest text-brand-navy">
              <span className="col-span-2">Issue Type</span>
              <span>Priority</span>
            </div>
            <div className="divide-y divide-gray-100">
              {reportingCategories.map(({ label, urgency }) => (
                <div key={label} className="grid grid-cols-3 px-5 py-3 items-center">
                  <span className="col-span-2 text-sm text-gray-700">{label}</span>
                  <span className={`text-xs px-2 py-1 rounded-full border w-fit capitalize ${urgencyColors[urgency]}`}>
                    {urgency === "emergency" ? "Emergency — Call 911" : urgency}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-5 bg-brand-navy/5 border border-brand-navy/20 rounded-xl p-5 flex items-start gap-4">
            <CheckCircle className="text-brand-green flex-shrink-0 mt-0.5" size={18} />
            <div>
              <p className="font-semibold text-brand-navy text-sm mb-1">Response Time Commitment</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>· High-priority reports: reviewed within <strong>24 hours</strong></li>
                <li>· Medium-priority: within <strong>72 hours</strong></li>
                <li>· Low-priority: within <strong>7 business days</strong></li>
              </ul>
            </div>
          </div>
        </section>

        {/* Resources */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6">External Safety Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: "National Domestic Violence Hotline", number: "1-800-799-7233", url: "https://www.thehotline.org" },
              { name: "Crisis Text Line", number: "Text HOME to 741741", url: "https://www.crisistextline.org" },
              { name: "RAINN Sexual Assault Hotline", number: "1-800-656-4673", url: "https://www.rainn.org" },
              { name: "Stalking Prevention, Awareness & Resource Center", number: null, url: "https://www.stalkingawareness.org" },
            ].map(({ name, number, url }) => (
              <a
                key={name}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-brand-navy/30 transition-colors shadow-sm"
              >
                <p className="font-semibold text-brand-navy text-sm">{name}</p>
                {number && <p className="text-brand-green text-sm font-mono mt-1">{number}</p>}
                <p className="text-xs text-gray-400 mt-1">{url.replace("https://", "")}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-4">Contact Trust & Safety</h2>
          <div className="bg-brand-navy/5 border border-brand-navy/20 rounded-2xl p-6 space-y-3">
            <p className="font-bold text-brand-navy">{COMPANY} — Trust & Safety Team</p>
            <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin size={13} />Fort Worth, TX, United States</div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail size={13} />
              <a href={`mailto:${EMAIL}?subject=Safety Report`} className="text-brand-green underline underline hover:underline">{EMAIL}</a>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              For non-urgent enquiries our response time is 2–3 business days. For urgent safety matters,
              please mark your subject line "URGENT — Safety."
            </p>
          </div>
          <div className="mt-4 flex gap-4 text-sm">
            <Link to="/privacy" className="text-brand-green underline underline hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-brand-green underline underline hover:underline">Terms of Service</Link>
            <Link to="/community" className="text-brand-green underline underline hover:underline">Community Guidelines</Link>
          </div>
        </section>

      </div>
    </div>
  );
}
